import { useEffect, useState } from 'react';

import {
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  makeStyles,
  Popover,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { Close } from '@material-ui/icons';
import PopupState, { bindPopover, bindTrigger } from 'material-ui-popup-state';
import { useSnackbar } from 'notistack';

import { ReactComponent as TagsV2Icon } from 'assets/images/tag-icon-v2.svg';
import SearchBar from 'components/SearchBar';
import { useAppDispatch, useAppSelector } from 'store';
import {
  fetchAddTagAtendimento,
  fetchRemoveTagAtendimento,
  updateListaDeiniciadosAlterarTagContato,
} from 'store/reducers/atendimentos';
import {
  fetchAddTagContato,
  fetchRemoveTagContato,
} from 'store/reducers/contatos';
import { fetchTags, TagInterface, tagsSelector } from 'store/reducers/tags';

import { TagItem } from './TagItem';

const useStyles = makeStyles(({ typography, palette, spacing }) => ({
  paper: {
    width: 250,
    display: 'flex',
    flexDirection: 'column',
    gap: spacing(1),
    padding: spacing(1.5, 0, 0.5, 1.5),
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    scrollbarGutter: 'stable',
    overflowX: 'hidden',
    minHeight: spacing(30),
    maxHeight: spacing(30),
    paddingRight: spacing(1),
  },
  triggerIcon: {
    width: spacing(2),
    height: spacing(2),

    '& path': {
      fill: palette.text.primary,
    },
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    paddingLeft: spacing(1),
  },
  chipLabel: {
    fontSize: typography.pxToRem(10),
    fontWeight: typography.fontWeightMedium as number,
    cursor: 'pointer',
    paddingLeft: spacing(1.5),
  },
}));

interface TagsV2Props {
  id?: string;
  tags: TagInterface[];
  isContato?: boolean;
  isContatoAtendimento?: boolean;
  atendimentoId?: string;
  disabled?: boolean;
  inline?: boolean;
  onSuccess?: (tag: TagInterface) => void;
}

export default function TagsV2({
  id,
  tags,
  isContato = false,
  isContatoAtendimento = false,
  atendimentoId,
  disabled = false,
  inline = false,
  onSuccess,
}: TagsV2Props) {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { tags: tagsList, loadingTags: isLoading } =
    useAppSelector(tagsSelector);

  const [searchTerm, setSearchTerm] = useState('');
  const [tagAdd, setTagAdd] = useState<string>();
  const [localTags, setLocalTags] = useState<TagInterface[]>(tags);

  // Sincroniza o estado local com as props quando elas mudam
  useEffect(() => {
    setLocalTags(tags);
  }, [tags]);

  const onSearchChange = (value: string) => {
    setSearchTerm(value);
  };

  const onResetSearch = () => {
    setSearchTerm('');
  };

  // Adicionar tag ao contato
  const addTagContato = (params: { tagId: string; contatoId: string }) => {
    setTagAdd(params.tagId);
    dispatch(fetchAddTagContato({ ...params, isContatoAtendimento }))
      .unwrap()
      .then(({ data: tag }) => {
        onSuccess?.(tag);
      })
      .catch(error => {
        if (error.response) enqueueSnackbar(error.response.data.message);
      });
  };

  // Adicionar tag ao atendimento
  const addTagAtendimento = (params: {
    atendimentoId: string;
    tagId: string;
  }) => {
    setTagAdd(params.tagId);
    dispatch(fetchAddTagAtendimento(params))
      .unwrap()
      .then(({ data: tag }) => {
        onSuccess?.(tag);
      })
      .catch(error => {
        if (error.response) enqueueSnackbar(error.response.data.message);
      });
  };

  // Adicionar tag ao contato do atendimento iniciado
  const addTagContatoDoAtendimento = (params: {
    atendimentoId: string;
    tag: TagInterface;
  }) => {
    dispatch(updateListaDeiniciadosAlterarTagContato(params));
  };

  // Adiciona tag a um [contato] ou [atendimento]
  const handleAddTag = (tag: TagInterface) => {
    const tagId = tag.id;

    if (isContato && id && tagId) {
      addTagContato({ contatoId: id, tagId });

      if (atendimentoId) addTagContatoDoAtendimento({ atendimentoId, tag });
    } else if (id && tagId) {
      addTagAtendimento({ atendimentoId: id, tagId });
    }
  };

  // Remove tag do contato do atendimento iniciado
  const removeTagContatoDoAtendimento = (params: {
    atendimentoId: string;
    tagId: string;
  }) => {
    dispatch(updateListaDeiniciadosAlterarTagContato(params));
  };

  // Remove uma tag de um [contato] ou [atendimento]
  const handleRemoveTag = (tagId?: string) => {
    if (!tagId || disabled) return;

    if (isContato && id) {
      dispatch(
        fetchRemoveTagContato({ tagId, contatoId: id, isContatoAtendimento }),
      );

      if (atendimentoId)
        removeTagContatoDoAtendimento({ atendimentoId, tagId });

      dispatch(fetchTags());
    } else if (id) {
      dispatch(fetchRemoveTagAtendimento({ tagId, atendimentoId: id }))
        .unwrap()
        .then(({ data: tag }) => {
          onSuccess?.(tag);
        });
      dispatch(fetchTags());
    }
  };

  // Toggle tag (adiciona ou remove)
  const handleToggleTag = (tag: TagInterface) => {
    const isTagSelected = localTags.some(t => t.id === tag.id);

    if (isTagSelected) {
      // Atualiza estado local imediatamente (UI otimista)
      setLocalTags(prev => prev.filter(t => t.id !== tag.id));
      handleRemoveTag(tag.id);
    } else {
      // Atualiza estado local imediatamente (UI otimista)
      setLocalTags(prev => [
        ...prev,
        { ...tag, name: tag.nome || '', color: tag.cor || '' },
      ]);
      handleAddTag(tag);
    }
  };

  const renderTriggerButton = () => (
    <Tooltip
      arrow
      title={isContato ? 'Tags do contato' : 'Tags do atendimento'}
    >
      <IconButton aria-label="Tags" size="small" style={{ padding: 8 }}>
        <TagsV2Icon className={classes.triggerIcon} />
      </IconButton>
    </Tooltip>
  );

  const renderInlineContent = () => {
    if (localTags.length > 0) {
      return localTags.map(tag => (
        <TagItem
          key={tag.id}
          tag={tag}
          tagAdd={tagAdd}
          onRemove={handleRemoveTag}
        />
      ));
    }
    return renderTriggerButton();
  };

  const renderReducedContent = () => {
    return (
      <Box display="flex" alignItems="center" gridGap={4}>
        {localTags.length > 0 && (
          <>
            <TagItem
              key={localTags[0].id}
              tag={localTags[0]}
              tagAdd={tagAdd}
              onRemove={handleRemoveTag}
            />

            {localTags.length > 1 && (
              <Chip
                style={{ cursor: 'pointer', margin: 4 }}
                label={`+${localTags.length - 1}`}
                size="small"
                variant="outlined"
              />
            )}
          </>
        )}

        {localTags.length < 2 && renderTriggerButton()}
      </Box>
    );
  };

  return (
    <PopupState variant="popover" popupId="popup-tags-v2">
      {popupState => (
        <Box onClick={e => e.stopPropagation()} width="fit-content">
          <Box
            display="flex"
            alignItems="center"
            gridGap={4}
            flexWrap="wrap"
            width="fit-content"
            {...bindTrigger(popupState)}
          >
            {inline ? renderInlineContent() : renderReducedContent()}

            {inline && localTags.length > 0 && renderTriggerButton()}
          </Box>

          <Popover
            {...bindPopover(popupState)}
            TransitionProps={{ onEnter: () => dispatch(fetchTags()) }}
            classes={{ paper: classes.paper }}
            onClick={e => e.stopPropagation()}
            onMouseEnter={() => setTagAdd('')}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
          >
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="space-between"
              gridGap={8}
              style={{ paddingRight: 16 }}
            >
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography
                  variant="body2"
                  style={{ fontWeight: 600, paddingLeft: 8 }}
                >
                  Tags
                </Typography>
                <IconButton
                  aria-label="close"
                  size="small"
                  onClick={popupState.close}
                >
                  <Close />
                </IconButton>
              </Box>

              <SearchBar
                value={searchTerm}
                onChange={onSearchChange}
                onCancelSearch={onResetSearch}
                placeholder="Pesquisar"
                debounceTimer={0}
                showSearchIcon
                size="small"
              />
            </Box>

            <List disablePadding classes={{ root: classes.list }}>
              {isLoading && (
                <ListItem disableGutters style={{ justifyContent: 'center' }}>
                  <CircularProgress color="inherit" size={24} />
                </ListItem>
              )}

              {!isLoading && tagsList.length === 0 && (
                <ListItem disableGutters>
                  <Typography variant="body2">Sem tags</Typography>
                </ListItem>
              )}

              {!isLoading &&
                tagsList
                  .filter(tag => tag.nome?.toLowerCase().includes(searchTerm))
                  .map(tag => (
                    <ListItem
                      disableGutters
                      dense
                      style={{ padding: 0 }}
                      key={tag.id}
                      button
                      disabled={disabled}
                      onClick={() => handleToggleTag(tag)}
                    >
                      <Box className={classes.row}>
                        <TagItem tag={tag} />
                        <Checkbox
                          checked={localTags.some(t => t.id === tag.id)}
                          size="small"
                          color="primary"
                          disabled={disabled}
                        />
                      </Box>
                    </ListItem>
                  ))}
            </List>
          </Popover>
        </Box>
      )}
    </PopupState>
  );
}
